import type { ConnState, ControllerManager } from './controller-manager';
import type { TransportManager } from './transport-manager';
import type { ConnectionManager } from './connection-manager';
import type { GatewayEntry } from './gateway-entry';
import type { FleetGateway } from '../../core/bridge/connect-client';

/**
 * WHAT ONE ROSTER STREAM CAN HONESTLY SAY ABOUT ITSELF.
 *
 *  pending — subscribed, nothing proven yet
 *  live    — the stream has delivered what it owes
 *  down    — the stream broke; the worker is retrying
 *
 * Not every stream can reach `pending` honestly. Controllers and dashboards end
 * their initial replay with an explicit completion marker, so an EMPTY roster
 * still arrives — "none" and "not yet" are distinguishable, and they start
 * `pending`. Models, integrations and cameras have no such marker: an empty
 * roster and an unanswered one are the same silence, so they start `live` and
 * report only what they CAN observe, a break. Claiming `pending` for them would
 * park every gateway with no integrations on "Connecting" forever — the same lie
 * as the one this exists to fix, pointed the other way.
 */
export type StreamHealth = 'pending' | 'live' | 'down';

export interface StreamHealthSource {
  readonly health: StreamHealth;
}

/**
 * THE ROSTERS A GATEWAY'S PAGES STAND ON — the ones whose absence makes a green
 * badge a lie. One list, so the home card, the tree and the detail page cannot
 * disagree about what "the data is here" means.
 *
 * Controllers are not listed: their snapshot already IS the base state below.
 * Cameras and plugin types are deliberately not listed either: a gateway with no
 * video engine answers its camera subscription with an error, and plugin types
 * only drive the Add-controller picker — neither is data that failed to arrive.
 */
export function dataStreams(parts: {
  dashboards?: StreamHealthSource;
  models?: StreamHealthSource;
  integrations?: StreamHealthSource;
}): StreamHealthSource[] {
  return [parts.dashboards, parts.models, parts.integrations].filter((s): s is StreamHealthSource => !!s);
}

/**
 * The gateway badge's AUTHORITATIVE tri-state, combining three signals so the
 * home card, the gateway detail page, and the nav tree all agree. Single source
 * of truth — never duplicate this logic per view.
 *
 *  1. INTENT first: if the user disconnected our frontend, it's 'disconnected'
 *     regardless of what the (now torn-down) streams last said.
 *  2. DATA-PLANE liveness: the ControllerManager flips 'connected' only once the
 *     controllers snapshot lands ('connecting' before it, after a re-connect, or
 *     after a stream error).
 *  3. On the CLOUD leg, "connected" only means we reached the cloud api-gateway —
 *     which serves a FROZEN mirror even when the box is OFF — so downgrade it to
 *     'connecting' unless the gateway is actually live (instant leaf presence via
 *     FleetGateway.status, or the ~45s enroll-heartbeat freshness via lastSeen).
 *     On a LAN leg we're talking to the box directly, so it's authoritative.
 *  4. If the fleet says the box is OFFLINE, say so. Not-live used to collapse into
 *     'connecting' whatever the reason, which was truthful — we ARE still retrying —
 *     but it made "the box is powered off" and "we're mid-reconnect" indis-
 *     tinguishable, forever, on a box that is simply switched off. We already hold
 *     the answer: FleetGateway.status is server-derived from leaf presence and
 *     arrives live at ~1 Hz. Reporting it is honesty, not a new signal.
 *
 * 'offline' is deliberately NOT 'disconnected': that word is reserved for the user's
 * own intent, and a box being off is not a choice anyone made. It is also not an
 * error — a customer powering a box down is normal, so the badge is gray, not red.
 */
function transportConnState(
  controllers: ControllerManager,
  transport: TransportManager,
  gateway: FleetGateway,
  connection: ConnectionManager,
): ConnState {
  if (connection.intent === 'disconnected') return 'disconnected';
  const cs = controllers.connectionState;
  // Intent is 'connected' (we passed the check above), so the worker is
  // auto-reconnecting FOREVER (supervise) — a down data-plane is 'connecting'
  // (reconnecting), NEVER 'disconnected'. Returning cs here was the bug: a single
  // transient stream blip set cs='disconnected', which (a) flashed a "Disconnected"
  // badge mid-reconnect and (b) INVERTED the Connect control — eff()='disconnected'
  // while intent stayed 'connected', so the button showed "Connect" but its
  // intent-keyed onClick called disconnect(). 'disconnected' is now reserved for the
  // ONE truthful case: the user chose to detach (handled by the intent check above).
  // The fleet's leaf-presence bit is the authority on whether the BOX is up — it is
  // the one signal here that does not come through the box itself, so it stays
  // truthful when the box is unreachable. Only trust an explicit 'offline'; 'unknown'
  // means the fleet doesn't know, which is not the same as "off".
  const boxOff = gateway.status === 'offline';
  if (cs === 'disconnected') return boxOff ? 'offline' : 'connecting';
  if (transport.transportInfo.path === 'lan' || cs !== 'connected') return cs;
  // `gateway.status`/`lastSeenMs` are now LIVE (the app-shell fleet stream keeps the
  // record fresh), so this gate actually fires: a box going dark flips status→offline
  // (fleet-control derives it, lastSeen-freshness gated) and the cloud leg drops to
  // 'connecting'. The 150s window mirrors fleet-control's OnlineWindowNs (≈3× the
  // ~45s edge re-enroll) so the UI and the backend agree on "fresh".
  const live =
    gateway.status === 'online' ||
    (gateway.status !== 'offline' && gateway.lastSeenMs > 0 && Date.now() - gateway.lastSeenMs < 150_000);
  // Not live: say WHY when we know. An explicit fleet 'offline' is the box being
  // off; anything else (unknown, or merely stale lastSeen) is genuinely "we don't
  // know yet" and stays 'connecting' — claiming offline on an absent signal would
  // be the same lie in the other direction.
  if (live) return cs;
  return boxOff ? 'offline' : 'connecting';
}

/**
 * 5. CONNECTED MEANS THE DATA ARRIVED, not that a socket answered.
 *
 * Everything above proves reachability — a controllers snapshot landed, the box is
 * live. That was the whole test, and it let the badge read green over a dashboard
 * roster that never delivered a single frame: controllers ride one KV bucket,
 * dashboards another, and only the first one was being asked. So a reachable
 * gateway is 'connected' only once every roster its pages stand on is `live`, and
 * falls back to 'connecting' — the worker IS retrying — while any is pending or
 * down. It never invents 'offline' or 'disconnected'; those stay the fleet's and
 * the user's to say.
 */
export function deriveConnState(
  controllers: ControllerManager,
  transport: TransportManager,
  gateway: FleetGateway,
  connection: ConnectionManager,
  streams: readonly StreamHealthSource[] = [],
): ConnState {
  const base = transportConnState(controllers, transport, gateway, connection);
  if (base !== 'connected') return base;
  return streams.every((s) => s.health === 'live') ? 'connected' : 'connecting';
}

/** Same, from a whole entry (home + tree, which hold the GatewayEntry). */
export function entryConnState(e: GatewayEntry): ConnState {
  return deriveConnState(e.controllers, e.transport, e.gateway, e.connection, dataStreams(e));
}
